// Copyright (c) 2022 by Move Closer

import { CartIdentifier } from '../../../../contexts'

import { CartModel } from '../../contracts/models'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const CART_ID_KEY = 'cart_id'
export const CART_NEWSLETTER_KEY = 'cart_newsletter'

/**
 * @author Agnieszka Zawadzka <agnieszka.zawadzka@movecloser.pl>
 */
export const ORDER_KEY = 'latest_order'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 */
export const CartServiceType = Symbol.for('ICartService')

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface UpdatedItem {
  quantity: number
  uid: string
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface CartAddedItemData {
  sku: string
  quantity: number
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface AddSimpleProductsToCartItem {
  data: CartAddedItemData
}

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl>
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface ICartService {
  /**
   * Adds product with passed-in sku to cart.
   */
  addToCart (cartId: CartIdentifier, sku: string, quantity?: number): Promise<CartModel>

  /**
   * Adds many products to cart
   */
  addManyToCart (cartId: CartIdentifier, products: Array<CartAddedItemData>): Promise<CartModel>

  /**
   * Adds any number of simple and group products to the cart at the same time.
   */
  addSimpleProductsToCart (cartId: CartIdentifier, products: Array<AddSimpleProductsToCartItem>): Promise<CartModel>

  /**
   * Applies coupon to passed in cart.
   * @param cartId - Current active cart id.
   * @param code - Coupon code.
   */
  applyCoupon (cartId: CartIdentifier, code: string): Promise<CartModel>

  /**
   * Removes previously-applied coupon from the cart
   * @param cartId - Current active cart id.
   */
  clearCoupons (cartId: CartIdentifier): Promise<CartModel>

  /**
   * Resolves new cart by a given id
   */
  resolveCart (id: CartIdentifier, tries?: number): Promise<CartModel>

  /**
   * Resolves new empty cart.
   */
  resolveEmptyCart (): Promise<CartModel>

  /**
   * Remove the passed in product from cart.
   *
   * @param cartId - Currently active cart
   * @param uid - Id of product in cart.
   */
  removeFromCart (cartId: CartIdentifier, uid: string): Promise<CartModel>

  /**
   * Loads cart by its id
   */
  loadCartByIdentifier (cartId: CartIdentifier): Promise<CartModel>

  /**
   * Load (creates if does not exist) customer cart.
   */
  loadCustomerCart (): Promise<CartModel>

  /**
   * Merges two carts.
   *
   * @param source - source cart id (usually it is local storage cart or cart of guest)
   * @param destination - destination cart id (usually cart of the user)
   */
  mergerCarts (source: CartIdentifier, destination: CartIdentifier): Promise<CartModel>

  /**
   * Updates the item in cart by its id
   * @param cartId - Currently active cart
   * @param uid - Identifier of product in cart (NOT SKU!)
   * @param quantity - New quantity of the item.
   */
  updateCartItem (cartId: CartIdentifier, uid: string, quantity: number): Promise<CartModel>

  /**
   * Updates the collection of items
   * @param cartId - Currently active cart
   * @param updatedItems - Collection of items uid with new quantity
   */
  updateCartItems (cartId: CartIdentifier, updatedItems: Array<UpdatedItem>): Promise<CartModel>
}

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export const CART_CONFIG_KEY = 'cart'

/**
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface CartModuleConfig {
  hasBundledProducts: boolean
}
